'use client';

import { Box, Flex, HStack, Input, Link, Stack, Text } from '@chakra-ui/react';
import { useSignIn } from '@clerk/nextjs';
import { useRollbar } from '@rollbar/react';
import React from 'react';
import { alreadySignedInSessionId } from '@/lib/clerk-sessions';
import { OtpStep } from '@/legacy-spa/components/confirmation/account/OtpStep';
import { isDroppedRequest } from '@/legacy-spa/components/confirmation/account/signinErrors';
import { markAuthLanding } from './UniversalSignIn';
import { VerifiedOnOtherDevicePanel } from '@/legacy-spa/components/guest-ui/EmailLinkPanels';
import {
  OrDivider,
  PillButton,
  ResendControl,
  StatusPanel,
  WatchdogSpinner,
} from '@/legacy-spa/components/guest-ui/primitives';

type FlowState =
  | { kind: 'chooser' }
  | { kind: 'otp'; channel: 'phone' | 'email' }
  | { kind: 'email-sent' }
  | { kind: 'password' }
  | { kind: 'not-found'; identifier: string }
  | { kind: 'other-device' };

const toPhoneE164 = (raw: string): string | null => {
  const bare = raw.replace(/\D/g, '');
  if (raw.trim().startsWith('+')) {
    return bare.length >= 8 && bare.length <= 15 ? `+${bare}` : null;
  }
  if (bare.length === 10) return `+1${bare}`;
  if (bare.length === 11 && bare.startsWith('1')) return `+${bare}`;
  return null;
};

const clerkError = (err: any, fallback: string) =>
  err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || fallback;

const clerkCode = (err: any): string | undefined => err?.errors?.[0]?.code;

/** Clerk rejecting what the guest typed. A typo is not an incident, so these report at info beside `identifier not found` rather than paging anyone. */
const USER_INPUT_CODES = new Set(['form_param_format_invalid', 'form_param_nil']);

/** The identifier NEVER reaches Rollbar — an email or phone typed into a sign-in box is exactly the data a third-party error service should not hold. Its shape is the whole diagnostic anyway: which channel, and what Clerk offered. */
const identifierShape = (raw: string): 'email' | 'phone' | 'other' => {
  if (raw.includes('@')) return 'email';
  if (raw.replace(/\D/g, '').length >= 7) return 'phone';
  return 'other';
};

const ErrorNote = ({ children }: { children: React.ReactNode }) => (
  <Box bg="red.50" border="1px solid" borderColor="red.100" borderRadius="6px" p="12px 14px">
    <Text fontSize="13px" lineHeight="19px" fontWeight={500} color="red.600">
      {children}
    </Text>
  </Box>
);

const BrandMark = ({ provider }: { provider: 'google' | 'facebook' }) => (
  <Flex
    align="center"
    justify="center"
    w="22px"
    h="22px"
    borderRadius="4px"
    bg="white"
    color={provider === 'google' ? '#4285f4' : '#1877f2'}
    fontWeight={700}
    fontSize="15px"
  >
    {provider === 'google' ? 'G' : 'f'}
  </Flex>
);

export const UniversalSignInFlow = ({
  returnUrl,
  onFindReservation,
  onAlreadySignedIn,
}: {
  returnUrl: string;
  onFindReservation: () => void;
  /** Clerk refused because this browser already holds a session — see the `session_exists` handling below. The host shows its signed-in panel. */
  onAlreadySignedIn?: () => void;
}) => {
  const { isLoaded, signIn, setActive } = useSignIn();
  const rollbar = useRollbar();
  const [state, setState] = React.useState<FlowState>({ kind: 'chooser' });
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [sentTo, setSentTo] = React.useState('');
  const [hasPassword, setHasPassword] = React.useState(false);
  const [hasEmailLink, setHasEmailLink] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [loadTimedOut, setLoadTimedOut] = React.useState(false);
  const linkFactorRef = React.useRef<any>(null);

  if (!isLoaded) {
    // Never a blank terminal surface if Clerk fails to initialize.
    return loadTimedOut ? (
      <Stack spacing={4} py={4}>
        <Text fontSize="14px" lineHeight="20px" color="gray.600">
          Sign-in is taking too long to load. Check your connection and try again.
        </Text>
        <PillButton w="100%" onClick={() => window.location.reload()}>
          Reload the page
        </PillButton>
      </Stack>
    ) : (
      <WatchdogSpinner label="Loading sign-in…" onTimeout={() => setLoadTimedOut(true)} />
    );
  }

  const backToChooser = () => {
    setState({ kind: 'chooser' });
    setError(null);
  };

  /** Clerk may resolve session_exists after the modal opens during session hydration; treat it as success. */
  const handledAsAlreadySignedIn = (err: any): boolean => {
    // Multi-session twin: the typed identifier already has a session on this browser — switch to it.
    const existing = alreadySignedInSessionId(err);
    if (existing) {
      setError(null);
      void setActive({ session: existing }).then(
        () => (onAlreadySignedIn ? onAlreadySignedIn() : window.location.assign(returnUrl)),
        () => window.location.reload(),
      );
      return true;
    }
    if (clerkCode(err) !== 'session_exists') return false;
    setError(null);
    // Still reported, at info: the underlying race is unfixed, and silencing it would hide whether it is getting better or worse.
    rollbar.info('[signin] submitted while already signed in', { surface: 'universal' });
    if (onAlreadySignedIn) onAlreadySignedIn();
    else window.location.assign(returnUrl);
    return true;
  };

  const sendLink = async (factor: any) => {
    linkFactorRef.current = factor;
    setSentTo(factor.safeIdentifier ?? identifier.trim());
    setError(null);
    setState({ kind: 'email-sent' });
    try {
      const { startEmailLinkFlow } = signIn.createEmailLinkFlow();
      const res = await startEmailLinkFlow({
        emailAddressId: factor.emailAddressId,
        redirectUrl: returnUrl,
      });
      const verification = res.firstFactorVerification;
      if (res.status === 'complete') {
        try {
          await setActive({ session: res.createdSessionId });
        } catch (err: any) {
          rollbar.warning('[signin] session adopt failed, reloading', {
            surface: 'universal',
            step: 'email_link_complete',
            clerkCode: clerkCode(err),
          });
          window.location.reload();
        }
        return;
      }
      if (verification.status === 'verified') {
        if (verification.verifiedFromTheSameClient() && res.createdSessionId) {
          // Link opened in another tab here — adopt now, don't wait on Clerk's cross-tab sync.
          try {
            await setActive({ session: res.createdSessionId });
          } catch (err: any) {
            rollbar.warning('[signin] session adopt failed, reloading', {
              surface: 'universal',
              step: 'email_link_cross_tab',
              clerkCode: clerkCode(err),
            });
            window.location.reload();
          }
        } else {
          // A different device (or a same-client verify with no session to adopt) cannot sign this tab in — never park on the "check your email" panel.
          setState({ kind: 'other-device' });
        }
        return;
      }
      if (verification.status === 'expired') {
        setState({ kind: 'chooser' });
        setError('That link expired before it was opened. Send a fresh one.');
        rollbar.info('[signin] email link expired before use', { surface: 'universal' });
      }
    } catch (err: any) {
      setState({ kind: 'chooser' });
      setError(clerkError(err, "We couldn't send a sign-in link just now."));
      const payload = {
        surface: 'universal',
        clerkCode: clerkCode(err),
        error: String(err),
      };
      if (isDroppedRequest(err)) rollbar.warning('[signin] email link send failed', payload);
      else rollbar.error('[signin] email link send failed', payload);
    }
  };

  const submitIdentifier = async () => {
    const raw = identifier.trim();
    if (!raw) {
      setError('Enter the email or mobile number on your account.');
      return;
    }
    const isPhone = !raw.includes('@') && raw.replace(/\D/g, '').length >= 7;
    const normalized = isPhone ? (toPhoneE164(raw) ?? raw) : raw;
    setBusy(true);
    setError(null);
    try {
      const si = await signIn.create({ identifier: normalized });
      const factors = (si.supportedFirstFactors ?? []) as any[];
      const phoneFactor = factors.find((f) => f.strategy === 'phone_code');
      const linkFactor = factors.find((f) => f.strategy === 'email_link');
      const codeFactor = factors.find((f) => f.strategy === 'email_code');
      const passwordFactor = factors.find((f) => f.strategy === 'password');
      setHasPassword(Boolean(passwordFactor));
      setHasEmailLink(Boolean(linkFactor));

      if (isPhone && phoneFactor) {
        await signIn.prepareFirstFactor({
          strategy: 'phone_code',
          phoneNumberId: phoneFactor.phoneNumberId,
        } as any);
        setSentTo(phoneFactor.safeIdentifier ?? normalized);
        setState({ kind: 'otp', channel: 'phone' });
      } else if (linkFactor) {
        await sendLink(linkFactor);
      } else if (codeFactor) {
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: codeFactor.emailAddressId,
        } as any);
        setSentTo(codeFactor.safeIdentifier ?? normalized);
        setState({ kind: 'otp', channel: 'email' });
      } else if (phoneFactor) {
        await signIn.prepareFirstFactor({
          strategy: 'phone_code',
          phoneNumberId: phoneFactor.phoneNumberId,
        } as any);
        setSentTo(phoneFactor.safeIdentifier ?? normalized);
        setState({ kind: 'otp', channel: 'phone' });
      } else if (passwordFactor) {
        setState({ kind: 'password' });
      } else {
        // Clerk hands an SSO-claimed domain ONLY connection strategies — no email code, no password — so the identifier form has nothing to drive. The social buttons on this same screen ARE the way in, so point at them instead of at the phone number.
        const ssoOnly = factors.some((f) =>
          ['enterprise_sso', 'saml', 'oauth_google', 'oauth_facebook', 'google_one_tap'].includes(
            f.strategy,
          ),
        );
        setError(
          ssoOnly
            ? 'That address is set up for single sign-on — try “Continue with Google” below, or use the email address on your booking.'
            : "We couldn't find a way to sign in to that account. Call 508-271-7603 and we'll sort it out.",
        );
        // THROWS NOTHING: the account exists and Clerk answered fine, we just can't drive any factor it offered. Until now the only trace of this dead end was the guest phoning us. `strategies` is the diagnosis — it says what Clerk had and we couldn't use.
        rollbar[ssoOnly ? 'warning' : 'error']('[signin] no usable first factor', {
          surface: 'universal',
          identifierShape: identifierShape(raw),
          ssoOnly,
          strategies: factors.map((f) => f.strategy).sort(),
        });
      }
    } catch (err: any) {
      const code = clerkCode(err);
      if (handledAsAlreadySignedIn(err)) return;
      if (code === 'form_identifier_not_found') {
        setState({ kind: 'not-found', identifier: normalized });
        rollbar.info('[signin] identifier not found', {
          surface: 'universal',
          identifierShape: identifierShape(raw),
        });
      } else {
        setError(clerkError(err, "We couldn't check that just now. Please try again."));
        const payload = {
          surface: 'universal',
          identifierShape: identifierShape(raw),
          clerkCode: code,
          error: String(err),
        };
        if (USER_INPUT_CODES.has(code ?? '')) {
          // Deliberately its own message, so a mistyped address stops sharing an item with real failures.
          rollbar.info('[signin] identifier rejected as malformed', payload);
        } else if (isDroppedRequest(err)) {
          rollbar.warning('[signin] identifier lookup failed', payload);
        } else {
          rollbar.error('[signin] identifier lookup failed', payload);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await signIn.create({ identifier: identifier.trim(), password });
      if (res.status === 'complete') {
        try {
          await setActive({ session: res.createdSessionId });
        } catch (err: any) {
          rollbar.warning('[signin] session adopt failed, reloading', {
            surface: 'universal',
            step: 'password',
            clerkCode: clerkCode(err),
          });
          window.location.reload();
        }
        return;
      }
      setError('We need one more step to finish signing you in.');
      // A correct password that still doesn't sign the guest in — the message names a step the screen cannot actually offer, so this is a dead end.
      rollbar.error('[signin] password complete but status not complete', {
        surface: 'universal',
        status: res.status,
      });
    } catch (err: any) {
      if (handledAsAlreadySignedIn(err)) return;
      setError(clerkError(err, "That password didn't match."));
      rollbar.info('[signin] password rejected', {
        surface: 'universal',
        clerkCode: clerkCode(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const oauth = async (strategy: 'oauth_google' | 'oauth_facebook') => {
    setError(null);
    try {
      markAuthLanding();
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: '/sso-callback',
        redirectUrlComplete: returnUrl,
      });
    } catch (err: any) {
      if (handledAsAlreadySignedIn(err)) return;
      setError(clerkError(err, 'That sign-in could not be started.'));
      rollbar.error('[signin] oauth redirect failed', {
        surface: 'universal',
        strategy,
        clerkCode: clerkCode(err),
      });
    }
  };

  if (state.kind === 'otp') {
    return <OtpStep mode="signIn" channel={state.channel} sentTo={sentTo} onBack={backToChooser} />;
  }

  if (state.kind === 'other-device') {
    return (
      <VerifiedOnOtherDevicePanel
        onSendFreshLink={() => {
          const factor = linkFactorRef.current;
          if (factor) sendLink(factor);
          else backToChooser();
        }}
        onSignInAnotherWay={backToChooser}
      />
    );
  }

  if (state.kind === 'email-sent') {
    return (
      <Stack spacing={4} w="100%">
        <Stack spacing="6px">
          <Text
            as="h2"
            fontSize={{ base: '24px', md: '24px' }}
            lineHeight="30px"
            letterSpacing="-0.4px"
            fontWeight={700}
            color="dmNavy.500"
            wordBreak="break-word"
          >
            Check {sentTo}
          </Text>
          <Text fontSize="14px" lineHeight="20px" color="gray.600">
            We sent a sign-in link. Open it on this device and you&apos;ll come straight back here.
          </Text>
        </Stack>
        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="6px" p="14px">
          <Text fontSize="13px" lineHeight="18px" fontWeight={500} color="dmNavy.500">
            Keep this tab open
          </Text>
          <Text fontSize="13px" lineHeight="18px" color="gray.600">
            This page will sign you in by itself once you tap the link. The link works for 15
            minutes.
          </Text>
        </Box>
        <Flex align="center" justify="space-between" gap={3}>
          <Text fontSize="13px" color="dmStorm.500">
            Nothing yet?
          </Text>
          <ResendControl
            onResend={() => {
              const factor = linkFactorRef.current;
              if (factor) sendLink(factor);
            }}
          />
        </Flex>
        <Box h="1px" bg="gray.200" />
        <Stack spacing={2}>
          {hasPassword && (
            <Link
              fontSize="13px"
              fontWeight={500}
              color="dmNavy.500"
              textDecoration="underline"
              textUnderlineOffset="3px"
              onClick={() => {
                setState({ kind: 'password' });
                setError(null);
              }}
            >
              Enter a password instead
            </Link>
          )}
          <Link
            fontSize="13px"
            fontWeight={500}
            color="dmNavy.500"
            textDecoration="underline"
            textUnderlineOffset="3px"
            onClick={backToChooser}
          >
            Use a different email or number
          </Link>
        </Stack>
      </Stack>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <StatusPanel
        heading="We don't have an account for that"
        body={
          <>
            Nothing matches{' '}
            <Text as="span" fontWeight={600} color="dmNavy.500">
              {state.identifier}
            </Text>
            . Guests often have an account under a different address or their mobile number.
          </>
        }
        actions={
          <>
            <PillButton pill="navy" w="100%" onClick={backToChooser}>
              Try another email or number
            </PillButton>
            <PillButton w="100%" onClick={onFindReservation}>
              Find my reservation instead
            </PillButton>
          </>
        }
      />
    );
  }

  if (state.kind === 'password') {
    return (
      <Stack spacing={4} w="100%">
        <Stack spacing="6px">
          <Text
            as="h2"
            fontSize="24px"
            lineHeight="30px"
            letterSpacing="-0.4px"
            fontWeight={700}
            color="dmNavy.500"
          >
            Enter your password
          </Text>
          <Text fontSize="14px" lineHeight="20px" color="gray.600" wordBreak="break-word">
            Signing in as {identifier.trim()}.
          </Text>
        </Stack>
        {error && <ErrorNote>{error}</ErrorNote>}
        <Input
          h="52px"
          borderRadius="6px"
          borderColor="gray.200"
          _focusVisible={{ borderColor: 'dmNavy.500', boxShadow: 'none' }}
          fontSize="16px"
          type="password"
          autoFocus
          autoComplete="current-password"
          placeholder="Your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submitPassword()}
        />
        <PillButton w="100%" isLoading={busy} loadingText="Signing in" onClick={submitPassword}>
          Sign in
        </PillButton>
        <Stack spacing={2}>
          {hasEmailLink && (
            <Link
              fontSize="13px"
              fontWeight={500}
              color="dmNavy.500"
              textDecoration="underline"
              textUnderlineOffset="3px"
              onClick={() => {
                const factor = linkFactorRef.current;
                if (factor) sendLink(factor);
              }}
            >
              Email me a sign-in link instead
            </Link>
          )}
          <Link
            fontSize="13px"
            fontWeight={500}
            color="dmNavy.500"
            textDecoration="underline"
            textUnderlineOffset="3px"
            onClick={backToChooser}
          >
            Use a different email or number
          </Link>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing="18px" w="100%">
      <Stack spacing="6px">
        <Text
          as="h2"
          fontSize={{ base: '26px', md: '24px' }}
          lineHeight={{ base: '31px', md: '30px' }}
          letterSpacing={{ base: '-0.5px', md: '-0.4px' }}
          fontWeight={700}
          color="dmNavy.500"
        >
          Sign in to Del Mar
        </Text>
        <Text
          fontSize={{ base: '15px', md: '14px' }}
          lineHeight={{ base: '21px', md: '20px' }}
          color="gray.600"
        >
          Your trips, your saved homes, and everything for your stay.
        </Text>
      </Stack>

      <Stack spacing={3}>
        <PillButton pill="hairline" w="100%" onClick={() => oauth('oauth_google')}>
          <HStack spacing={3}>
            <BrandMark provider="google" />
            <Text fontSize="15px" fontWeight={500}>
              Continue with Google
            </Text>
          </HStack>
        </PillButton>
        <PillButton pill="hairline" w="100%" onClick={() => oauth('oauth_facebook')}>
          <HStack spacing={3}>
            <BrandMark provider="facebook" />
            <Text fontSize="15px" fontWeight={500}>
              Continue with Facebook
            </Text>
          </HStack>
        </PillButton>
      </Stack>

      <OrDivider />

      <Stack spacing="5px">
        <Text fontSize="12px" lineHeight="16px" fontWeight={500} color="gray.700">
          Email or mobile number
        </Text>
        <Input
          h="52px"
          borderRadius="6px"
          borderColor="gray.200"
          _focusVisible={{ borderColor: 'dmNavy.500', boxShadow: 'none' }}
          fontSize="16px"
          autoFocus
          autoComplete="username"
          inputMode="email"
          placeholder="you@example.com or (508) 000-0000"
          value={identifier}
          onChange={(e) => {
            setIdentifier(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && submitIdentifier()}
        />
        <Text fontSize="12px" lineHeight="17px" color="dmStorm.500">
          Whichever one you used to set up your account.
        </Text>
      </Stack>

      {error && <ErrorNote>{error}</ErrorNote>}

      <PillButton w="100%" isLoading={busy} loadingText="Checking" onClick={submitIdentifier}>
        Continue
      </PillButton>
    </Stack>
  );
};
